/**
 * Cache-first store for the coach's clients list (mirrors utils/chatCache).
 *
 * Lets the Clients dashboard render the last known list instantly when the coach
 * navigates back to it, while a fresh copy is fetched in the background. Keyed by
 * coach user id so different coaches on the same browser don't collide.
 */

// Bump when the cached student shape changes so stale-shaped entries are ignored
// instead of rendering with missing fields.
const CACHE_VERSION = 1;

const PREFIX = 'kaiylo_students_';
const key = (coachId) => `${PREFIX}${coachId}`;

/**
 * @param {string} coachId
 * @returns {{ data: Array, timestamp: number } | null}
 */
export function getCachedStudents(coachId) {
  if (!coachId) return null;
  try {
    const raw = localStorage.getItem(key(coachId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== CACHE_VERSION || !Array.isArray(parsed.data)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Persist the list. An empty list is never stored: it could be a transient or
 * erroneous empty response, and a genuine "0 clients" shouldn't keep showing a
 * stale list — so we clear the key instead.
 * @param {string} coachId
 * @param {Array} students
 */
export function setCachedStudents(coachId, students) {
  if (!coachId || !Array.isArray(students)) return;
  try {
    if (students.length === 0) {
      localStorage.removeItem(key(coachId));
      return;
    }
    localStorage.setItem(
      key(coachId),
      JSON.stringify({ version: CACHE_VERSION, data: students, timestamp: Date.now() })
    );
  } catch {
    // Best-effort cache — ignore quota / serialization errors.
  }
}

// Compteurs du tableau de bord (vidéos à corriger, messages non lus, prochaine
// séance, bloc en cours), gardés avec la liste : le tri par priorité en dépend,
// et sans eux une liste servie depuis le cache s'affichait dans un ordre puis se
// réordonnait à leur arrivée. Même préfixe, donc effacés aussi à la déconnexion.
const countsKey = (coachId) => `${PREFIX}counts_${coachId}`;

/**
 * @param {string} coachId
 * @returns {{ videoCounts: object, messageCounts: object, nextSessions: object, currentBlocks: object } | null}
 */
export function getCachedDashboardCounts(coachId) {
  if (!coachId) return null;
  try {
    const raw = localStorage.getItem(countsKey(coachId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== CACHE_VERSION || typeof parsed.data !== 'object') return null;
    return parsed.data;
  } catch {
    return null;
  }
}

/**
 * @param {string} coachId
 * @param {{ videoCounts: object, messageCounts: object, nextSessions: object, currentBlocks: object }} counts
 */
export function setCachedDashboardCounts(coachId, counts) {
  if (!coachId || !counts) return;
  try {
    localStorage.setItem(countsKey(coachId), JSON.stringify({ version: CACHE_VERSION, data: counts, timestamp: Date.now() }));
  } catch {
    // Best-effort cache.
  }
}

/**
 * @param {string} coachId
 */
export function clearCachedStudents(coachId) {
  if (!coachId) return;
  try {
    localStorage.removeItem(key(coachId));
    localStorage.removeItem(countsKey(coachId));
  } catch {
    // no-op
  }
}

/**
 * Remove every coach's cached clients list. Called on logout to avoid leaving
 * client names/emails at rest and to prevent stale data when switching accounts.
 */
export function clearAllCachedStudents() {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    // no-op
  }
}
